import type { TransformedTarget } from '../../../../api/targets-client/targets-client.type';
import type {
  MeasureDataResponse,
  MeasureMetadata,
} from '../../../../api/work-periods-client/work-periods-client.type';
import { Measure } from '../../../../api/work-periods-client/work-periods-client.type';

/**
 * Merges measure metadata with their corresponding targets.
 *
 * @param {MeasureMetadata[]} measureMetaData - Array of measure metadata objects
 * @param {TransformedTarget[]} targets - Array of transformed target objects
 * @param {boolean} [onlyWithTargets=false] - If true, only include measures that have targets
 * @returns {MeasureMetadata[]} Array of measure metadata with targets merged in
 */
const mergeMeasureMetaDataAndTargets = (
  measureMetaData: MeasureMetadata[],
  targets: TransformedTarget[],
  onlyWithTargets = false,
): MeasureMetadata[] => {
  const measureMetaDataMap = measureMetaData.reduce(
    (acc, metadata) => {
      acc[metadata.measure_name] = { ...metadata, targets: [] };
      return acc;
    },
    {} as Record<string, MeasureMetadata>,
  );

  targets.forEach((target) => {
    if (measureMetaDataMap[target.measure]) {
      measureMetaDataMap[target.measure].targets?.push(target);
    }
  });

  if (onlyWithTargets) {
    return Object.values(measureMetaDataMap).filter((metadata) => metadata.targets && metadata.targets.length > 0);
  }

  return Object.values(measureMetaDataMap);
};

/**
 * Checks if a measure has no valid data.
 *
 * @param {MeasureDataResponse} data - The measure data response object
 * @param {Measure} measure - The measure to check
 * @returns {boolean} True if the measure has no valid data, false otherwise
 */
const isEmptyMeasureData = (data: MeasureDataResponse, measure: Measure): boolean => {
  const validValues = Object.values(data[measure] || {}).filter((value) => value !== null && value !== undefined);

  return validValues.length === 0;
};

/**
 * Checks if all selected measures have empty data.
 *
 * @param {MeasureDataResponse} data - The measure data response object
 * @param {Measure[]} selectedMeasures - Array of selected measures to check
 * @returns {boolean} True if all selected measures have empty data, false otherwise
 */
const isEmptySelectedData = (data: MeasureDataResponse, selectedMeasures: Measure[]): boolean => {
  return selectedMeasures.every((measure) => isEmptyMeasureData(data, measure));
};

export { isEmptySelectedData, mergeMeasureMetaDataAndTargets };
